import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, Divider, Button, Chip, Stack } from '@mui/material';
import * as billCollectApi from '../../lib/api/billCollect';
import BillCollectDialog from '../../containers/projectmng/billcollect/BillCollectDialog';

const Row = ({ label, value }) => (
  <>
    <Grid item xs={3}>
      <Typography variant="subtitle2" component="div">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={3}>
      <Typography variant="body2" component="div">
        {value === null || value === undefined || value === '' ? '-' : value}
      </Typography>
    </Grid>
  </>
);

const EVIDENCE_LABEL = { TAX_INVOICE: '세금계산서' };

// 과세구분: 부가세>0 → 과세 / (공급가액>0 & 부가세=0) → 영세(영세율) / 금액 0 → 구분불가('-')
export const taxKind = (bill) => {
  const vat = Number(bill?.invVat) || 0;
  const amt = Number(bill?.invAmt) || 0;
  if (vat > 0) return '과세';
  if (amt > 0) return '영세';
  return '-';
};
// 외환 거래 여부
export const isForeign = (bill) =>
  bill?.osTran === 'Y' || (!!bill?.currencyType && bill.currencyType !== '');

const BillFlowDetail = ({ bill, onCollectChanged }) => {
  const [summary, setSummary] = useState(null);
  const [openCollect, setOpenCollect] = useState(false);

  const invUuid = bill?.invUuid;
  const isPurchase = bill?.invTyp === '매입';
  const term = isPurchase ? '지급' : '수금';

  // 대금은 한 번에 안 들어오고 나눠 들어온다. 현황을 상세에서 바로 보여준다.
  const loadSummary = useCallback(async () => {
    if (!invUuid) return;
    try {
      const res = await billCollectApi.getCollectSummary(invUuid);
      setSummary(res?.data?.data || null);
    } catch (e) {
      // 조회 실패로 상세 화면 전체가 막히지는 않게 한다.
      setSummary(null);
    }
  }, [invUuid]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  if (!bill) return null;
  const won = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString());

  return (
    <Box sx={{ px: 2 }}>
      <Grid container spacing={1} sx={{ py: 1 }}>
        <Row label="계산서번호" value={bill.invNo} />
        <Row label="유형" value={bill.invTyp} />
        <Row label="고객명" value={bill.partnerNm} />
        <Row label="사업명" value={bill.cstNm} />
        <Row label="공급가액" value={won(bill.invAmt)} />
        <Row label="부가세" value={won(bill.invVat)} />
        <Row label="합계" value={won(bill.invTot)} />
        <Row label="과세구분" value={taxKind(bill)} />
        <Row label="증빙분류" value={EVIDENCE_LABEL[bill.evidenceType] || bill.evidenceType} />
      </Grid>

      {isForeign(bill) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={1} sx={{ py: 1 }}>
            <Row label="해외거래" value="외환결제" />
            <Row label="통화" value={bill.currencyType} />
            <Row label="외환금액" value={bill.foreignAmount} />
            <Row label="환차익" value={won(bill.exchangeProfit)} />
          </Grid>
        </>
      )}

      <Divider sx={{ my: 1 }} />
      <Grid container spacing={1} sx={{ py: 1 }}>
        <Row label="발행예정일" value={bill.reqPlanDt} />
        <Row label="발행일" value={bill.reqCompDt} />
        <Row label="수금/지급 예정일" value={bill.procPlanDt} />
        <Row label="수금/지급일" value={bill.procCompDt} />
        <Row label="지급종류" value={bill.procTyp} />
        <Row label="실적확정일" value={bill.rsltCompDt} />
      </Grid>

      {/* 부분 수금/지급.
          완료 버튼 하나로는 "얼마가 들어왔는지" 를 표현할 수 없어 여기에 현황과 내역을 붙인다. */}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {term} 현황
          </Typography>
          {summary && (
            <>
              <Typography variant="body2" color="text.secondary">
                {term}액 <strong>{won(summary.collectedAmt)}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                잔액 {won(summary.remainAmt)}
              </Typography>
              <Chip
                size="small"
                label={summary.completed ? `${term} 완료` : `미${term}`}
                color={summary.completed ? 'success' : 'warning'}
                variant={summary.completed ? 'outlined' : 'filled'}
              />
            </>
          )}
          {/* 오른쪽 끝으로 밀어두니 화면이 좁을 때 잘려서 안 보였다.
              부분 수금이 이 화면의 주 작업이라 현황 바로 옆에 둔다. */}
          <Button
            size="small"
            variant="contained"
            disabled={bill.reqCompYn !== 'Y'}
            onClick={() => setOpenCollect(true)}
          >
            {term} 내역 · 등록
          </Button>
          <Box sx={{ flexGrow: 1 }} />
        </Stack>
        {bill.reqCompYn !== 'Y' && (
          // 왜 못 누르는지만 알려주면 다음에 뭘 해야 할지 모른다. 다음 단계를 같이 적는다.
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            아래에서 <strong>발급완료</strong>를 먼저 처리하면 {term} 등록이 열립니다.
          </Typography>
        )}
      </Box>

      <BillCollectDialog
        open={openCollect}
        invUuid={invUuid}
        invTyp={bill.invTyp}
        onClose={() => setOpenCollect(false)}
        onSaved={() => {
          loadSummary();
          if (onCollectChanged) onCollectChanged();
        }}
      />
    </Box>
  );
};

export default BillFlowDetail;
